<template>
  <div class="messages-conversations-filter pt-2 pl-2 pr-2">
    <div class="input-icon mb-3">
      <input
        type="text"
        class="form-control"
        v-model="query"
        @input="handleSearchDebounced"
        :placeholder="t('messages.search_placeholder')"
      />
      <span class="input-icon-addon">
        <i class="fe fe-search"></i>
      </span>
    </div>
  </div>
  <div class="messages-conversations">
    <div class="wrapper-conversations">
      <div class="conversations list-group list-group-flush">
        <a
          href="#"
          v-for="item in filteredConversations"
          :key="item.id"
          @click.prevent="$emit('select', item.id)"
          class="conversation list-group-item list-group-item-action flex-column align-items-start d-block p-3"
          :class="{
            premium: item.contact?.premium,
            active: item.id === currentContactId,
          }"
        >
          <div class="d-flex w-100 justify-content-start align-content-center">
            <div class="pr-3 align-self-center">
              <div
                class="avatar"
                :style="{ backgroundImage: item.contact?.avatar ? `url(${item.contact.avatar})` : 'none' }"
              >
                <span class="avatar-status" :class="onlineClass(item.contact?.id, item.contact?.online)" v-if="hasOnlineIndicator(item.contact?.id, item.contact?.online)"></span>
              </div>
            </div>
            <div class="align-self-center flex-grow-1">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h5 class="name mb-0">
                  {{ item.contact?.full_name }}<span v-if="item.contact?.age">, {{ item.contact.age }}</span>
                </h5>
                <small class="text-muted" v-if="item.last_message?.timestamp">
                  {{ relativeTime(item.last_message.timestamp) }}
                </small>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <div class="message" :class="{ 'text-muted font-italic': isTyping(item.id) }">
                  <span v-if="isTyping(item.id)" class="typing-header-indicator">
                    <span class="typing-header-indicator-text">{{ t('messages.typing') }}</span>
                    <span class="typing-header-dots" aria-hidden="true"><span></span><span></span><span></span></span>
                  </span>
                  <span v-else-if="getDraftText(item.id)" class="draft-text"><span class="text-danger draft-label">{{ t('messages.draft') }}: </span>{{ truncateText(getDraftText(item.id)) }}</span>
                  <span v-else-if="item.last_message?.hasMedia" class="d-flex align-items-center"><i class="fe fe-image mr-1"></i>{{ t('messages.photo') }}</span>
                  <span v-else-if="item.last_message?.text">{{ truncateText(item.last_message.text) }}</span>
                </div>
                <span
                  class="badge badge-danger badge-pill ml-2 nav-badge sidebar-unread-badge"
                  v-if="unread(item.id)"
                  :data-contact-id="item.id"
                >
                  {{ unread(item.id) }}
                </span>
                
                                
                <small v-if="item.last_message?.type === 'sent' && !unread(item.id)" class="message-status-time">
                  <i v-if="item.last_message?.status === 'pending'" class="fa fa-spinner fa-spin" :title="t('messages.sending')"></i>
                  <span v-else-if="item.last_message?.status === 'read' || item.last_message?.is_read === true || item.last_message?.is_read === 1" class="message-status-read" :title="t('messages.read_status')">
                    <i class="fa fa-check"></i>
                    <i class="fa fa-check"></i>
                  </span>
                  <span v-else-if="item.last_message?.status === 'delivered'" class="message-status-delivered" :title="t('messages.delivered_status')">
                    <i class="fa fa-check"></i>
                    <i class="fa fa-check"></i>
                  </span>
                  <i v-else-if="item.last_message?.status && item.last_message?.status !== 'unknown'" class="fa fa-check text-muted" :title="t('messages.status_prefix', { status: item.last_message?.status })"></i>
                </small>
              </div>
            </div>
          </div>
        </a>
      </div>
      
      <div v-if="!filteredConversations.length" class="conversations-empty-state d-flex flex-column align-items-center justify-content-center text-center p-5">
        <div class="mb-3">
          <i class="flaticon1-chat" style="font-size: 48px; color: var(--conversation-muted-color, #dee2e6);"></i>
        </div>
        <h5 class="text-muted">
          {{ query ? t('messages.no_results_found') : t('messages.no_conversations_yet') }}
        </h5>
        <p class="text-muted">
          {{ query ? t('messages.try_different_name') : t('messages.start_chatting') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from '@/composables/useI18n';
import { useMessageDrafts } from '@/composables/useMessageDrafts';
import { processSidebarText } from '@/utils/textHelpers';

const { t } = useI18n();
const { loadDraft } = useMessageDrafts();

const getDraftText = (contactId: string | number) => loadDraft(contactId);

const props = defineProps<{
  conversations: any[];
  currentContactId: string | number | null;
  typing?: Record<string, any>;
  unreadCount?: Record<string, number>;
  presence?: Record<string | number, string>;
}>();

const emit = defineEmits<{ (e: 'select', id: string | number): void; (e: 'search', query: string): void }>();

const query = ref('');
let searchTimeout: number | null = null;

const handleSearchDebounced = () => {
  // Debounce search for better UX
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  
  searchTimeout = window.setTimeout(() => {
    emit('search', query.value);
  }, 300);
};

const filteredConversations = computed(() => {
  // Deduplicate conversations
  const seen = new Set<string | number>();
  const unique = (props.conversations || []).filter((c) => {
    const id = c?.id ?? c?.contact?.id;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  if (!query.value) return unique;
  const q = query.value.toLowerCase();
  return unique.filter((c) =>
    (c.contact?.full_name || '').toLowerCase().includes(q)
  );
});

const relativeTime = (ts: number) => {
  // Use TimezoneHelper for relative time formatting
  if (window.TimezoneHelper && typeof window.TimezoneHelper.formatRelativeTime === 'function') {
    return window.TimezoneHelper.formatRelativeTime(ts);
  }
  
  // Fallback to simple formatting if TimezoneHelper unavailable
  try {
    const now = Date.now() / 1000;
    const diff = now - ts;
    const absDiff = Math.abs(diff);

    if (absDiff < 60) return t('messages.time_now');
    if (absDiff < 3600) return Math.floor(absDiff / 60) + ' ' + t('messages.time_minutes_short');
    if (absDiff < 86400) return Math.floor(absDiff / 3600) + ' ' + t('messages.time_hours_short');
    if (absDiff < 604800) {
      const days = Math.floor(absDiff / 86400);
      return days === 1 ? t('messages.time_yesterday') : days + ' ' + t('messages.time_days_short');
    }

    const date = new Date(ts * 1000);
    const today = new Date();
    const thisYear = today.getFullYear();

    if (date.getFullYear() === thisYear) {
      const monthKeys = [
        'messages.month_jan', 'messages.month_feb', 'messages.month_mar',
        'messages.month_apr', 'messages.month_may', 'messages.month_jun',
        'messages.month_jul', 'messages.month_aug', 'messages.month_sep',
        'messages.month_oct', 'messages.month_nov', 'messages.month_dec'
      ];
      return date.getDate() + ' ' + t(monthKeys[date.getMonth()]);
    } else {
      return date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear();
    }
  } catch (e) {
    console.error('Error formatting relative time:', ts, e);
    return '';
  }
};

const unread = (contactId: string | number) => {
  return props.unreadCount?.[contactId] || 0;
};

const isTyping = (contactId: string | number) => {
  const t = props.typing?.[contactId];
  if (!t) return false;
  return Object.values(t).some((v: any) => v?.isTyping);
};

const getOnlineStatus = (contactId: string | number | undefined, fallback?: boolean | string): string => {
  if (contactId === undefined || contactId === null) return fallback === true ? 'online' : (typeof fallback === 'string' ? fallback : 'offline');
  if (props.presence && contactId in props.presence) return props.presence[contactId] as string;
  if (fallback === true) return 'online';
  if (typeof fallback === 'string') return fallback;
  return 'offline';
};

const hasOnlineIndicator = (contactId: string | number | undefined, fallback?: boolean | string): boolean => {
  const status = getOnlineStatus(contactId, fallback);
  return status === 'online' || status === 'recently_online';
};

const onlineClass = (contactId: string | number | undefined, fallback?: boolean | string): string => {
  const status = getOnlineStatus(contactId, fallback);
  if (status === 'online') return 'bg-green';
  if (status === 'recently_online') return 'bg-orange';
  return 'bg-gray';
};

const truncateText = processSidebarText;

watch(query, handleSearchDebounced);
</script>

<style scoped>
.conversation {
  transition: background-color 0.2s ease;
}


.conversation.active {
  background-color: var(--conversation-active-bg, #e3f2fd);
}

.avatar {
  position: relative;
  display: inline-block;
}

.avatar-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border: 2px solid var(--avatar-status-border, #fff);
  border-radius: 50%;
}

.name {
  font-size: 16px;
  font-weight: 600;
  color: var(--conversation-name-color, #333);
}

.premium .name {
  color: var(--conversation-premium-color, #ffc107);
}

.message-status-time {
  font-size: 0.75rem;
  color: var(--conversation-muted-color, #6c757d);
  white-space: nowrap;
}

.message-status-read,
.message-status-delivered {
  display: inline-flex;
  align-items: center;
  position: relative;
  font-size: 0.7rem;
  margin-right: 3px;
}

.message-status-read i:first-child,
.message-status-delivered i:first-child {
  position: relative;
  z-index: 1;
}

.message-status-read i:last-child,
.message-status-delivered i:last-child {
  position: relative;
  z-index: 2;
}

.message-status-read {
  color: var(--conversation-read-color, #007bff);
}

.message-status-delivered {
  color: var(--conversation-delivered-color, #6c757d);
}

.message-status-read i,
.message-status-delivered i {
  animation: checkmark-appear 0.3s ease-in-out;
}

@keyframes checkmark-appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.conversation.active .text-muted {
  color: inherit !important;
}

.conversation.active .fa-check.text-muted {
  color: var(--conversation-delivered-color, #6c757d) !important;
}

/* Ensure text column can shrink below its content */
.conversation .flex-grow-1 {
  min-width: 0;
}

/* Single-line ellipsis for last message text */
.message {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1 1 auto;
}
</style>
